import { useEffect } from 'react';

import { DateFormat } from '@blockworks/ui/legacy/datetime';
import { ImageWithFallback, ExpandableImage } from '@blockworks/ui/legacy/image';
import { RenderSimpleHtml } from '@blockworks/ui/legacy/string';
import { useLocalStorage } from '@blockworks/platform/hooks';
import Image from 'next/image';
import { useIdentify, useTrack } from '@blockworks/platform/services/analytics';

import Breadcrumb from '@/components/display/headers/breadcrumb';
import GravityForm from '@/components/form/gravity/gravity-form';
import type { Webinar } from '@/types/article-type';
import UserIdentificationData from '@/components/pages/webinar/layout/type/user-identification-data';

type WebinarPageLayoutProps = {
    webinar: Webinar;
};

const formatFormData = (formData: any[]): UserIdentificationData => {
    if (!formData || formData.length === 0 || Object.keys(formData).length === 0) {
        return {};
    }

    let userData = {};
    formData.forEach(data => {
        userData = {
            ...userData,
            [data.label.toLowerCase()]: data.value,
        };
    });

    return userData as UserIdentificationData;
};

const WebinarPageLayout = (props: WebinarPageLayoutProps) => {
    const { webinar } = props;
    const track = useTrack();
    const identify = useIdentify();
    const { setValue: setUserData } = useLocalStorage<UserIdentificationData | null>('user.identify');

    // Find out form id from customfields html response
    const formIdMatch = webinar.customFields.form?.match(/'gform_([0-9]+)'/im);
    const formId: string | null = formIdMatch && formIdMatch?.length > 1 ? (formIdMatch[1]! as string) : null;

    useEffect(() => {
        if (!formId) {
            // eslint-disable-next-line no-console
            console.error('Form not found.', webinar.customFields.form);
        }
    }, [formId]);

    const onFormSubmit = (formData: any, data: any) => {
        try {
            const formattedUserData = formatFormData(Object.values(formData));
            setUserData(formattedUserData);
            if (formattedUserData.email) {
                identify(formattedUserData.email!, {
                    name: formattedUserData.name || '',
                    company: formattedUserData.company || '',
                    jobTitle: formattedUserData['job title'] || '',
                });
            }
        } catch (error) {
            console.error(error);
        }

        track('Webinar.Submit', {
            data: {
                ...data,
                webinarSlug: webinar.slug,
                webinarTitle: webinar.title,
                webinarImage: webinar.imageUrl,
                webinarDate: webinar.eventStartDate,
                webinarSpeakers: webinar.customFields?.speakers
                    ? webinar.customFields?.speakers?.map(s => s.name).join(', ')
                    : '',
            },
            formData,
        });
    };

    return (
        <main className="border-gray-200 w-full p-6 md:p-8 lg:p-12 xl:p-24">
            <div className="mx-auto max-w-6xl align-center">
                <div className="w-full flex flex-col justify-start items-start gap-10">
                    <div className="flex flex-col justify-start items-start gap-5">
                        <Breadcrumb
                            items={[
                                {
                                    title: 'Roundtables',
                                    link: '/roundtables',
                                },
                            ]}
                        />
                        <p className="flex-grow-0 flex-shrink-0 text-[52px] font-semibold text-left text-[#110b29]">
                            {webinar.title}
                        </p>
                        <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 gap-5">
                            <div className="flex justify-start items-center gap-2">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M4.33333 3.66667V1M9.66667 3.66667V1M3.66667 6.33333H10.3333M2.33333 13H11.6667C12.403 13 13 12.403 13 11.6667V3.66667C13 2.93029 12.403 2.33333 11.6667 2.33333H2.33333C1.59695 2.33333 1 2.93029 1 3.66667V11.6667C1 12.403 1.59695 13 2.33333 13Z"
                                        stroke="#110B29"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                                <div className="flex justify-start items-center gap-1">
                                    <p className="flex-grow-0 flex-shrink-0 text-sm text-left uppercase text-[#110b29]">
                                        <DateFormat dateStringOrTimestamp={webinar.eventStartDate} />
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-start items-center gap-2">
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M8.205 8.895L8.88 8.22L6.495 5.82V2.805H5.595V6.18L8.205 8.895ZM6 12C5.18 12 4.405 11.8425 3.675 11.5275C2.945 11.2125 2.3075 10.7825 1.7625 10.2375C1.2175 9.6925 0.7875 9.055 0.4725 8.325C0.1575 7.595 0 6.82 0 6C0 5.18 0.1575 4.405 0.4725 3.675C0.7875 2.945 1.2175 2.3075 1.7625 1.7625C2.3075 1.2175 2.945 0.7875 3.675 0.4725C4.405 0.1575 5.18 0 6 0C6.82 0 7.595 0.1575 8.325 0.4725C9.055 0.7875 9.6925 1.2175 10.2375 1.7625C10.7825 2.3075 11.2125 2.945 11.5275 3.675C11.8425 4.405 12 5.18 12 6C12 6.82 11.8425 7.595 11.5275 8.325C11.2125 9.055 10.7825 9.6925 10.2375 10.2375C9.6925 10.7825 9.055 11.2125 8.325 11.5275C7.595 11.8425 6.82 12 6 12ZM6 11.1C7.4 11.1 8.6 10.6 9.6 9.6C10.6 8.6 11.1 7.4 11.1 6C11.1 4.6 10.6 3.4 9.6 2.4C8.6 1.4 7.4 0.9 6 0.9C4.6 0.9 3.4 1.4 2.4 2.4C1.4 3.4 0.9 4.6 0.9 6C0.9 7.4 1.4 8.6 2.4 9.6C3.4 10.6 4.6 11.1 6 11.1Z"
                                        fill="#110B29"
                                    ></path>
                                </svg>
                                <p className="flex-grow-0 flex-shrink-0 text-sm text-left uppercase text-[#110b29]">
                                    <DateFormat dateStringOrTimestamp={webinar.eventStartDate} format="p (z)" />
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="self-stretch flex-grow-0 flex-shrink-0 text-xl text-left text-black">
                        <RenderSimpleHtml html={webinar.content} />
                    </div>
                    {webinar.customFields?.sponsored_by && (
                        <div className="flex justify-center items-center gap-3">
                            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-1">
                                <div className="flex justify-start items-center gap-1">
                                    <p className="flex-grow-0 flex-shrink-0 text-md text-left text-[#6a6a6a]">
                                        Sponsored by{' '}
                                        {webinar.customFields?.sponsored_by_image &&
                                        webinar.customFields?.sponsored_by_link?.url ? (
                                            <div className="w-[200px] mt-2">
                                                <a
                                                    href={webinar.customFields?.sponsored_by_link?.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <Image
                                                        src={webinar.customFields?.sponsored_by_image}
                                                        alt={webinar.customFields?.sponsored_by ?? ''}
                                                        width={200}
                                                        height={60}
                                                    />
                                                </a>
                                            </div>
                                        ) : (
                                            <span className="text-black">{webinar.customFields?.sponsored_by}</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <section className="py-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 place-content-center">
                        {webinar.customFields.speakers &&
                            webinar.customFields.speakers.map(speaker => (
                                <div
                                    key={speaker.name}
                                    className="flex justify-start items-start flex-grow relative gap-3"
                                >
                                    {speaker.image && (
                                        <div className="w-[100px] h-[100px]">
                                            <ExpandableImage>
                                                <ImageWithFallback
                                                    alt="speaker"
                                                    src={speaker.image.sizes?.medium || ''}
                                                    width={speaker.image.sizes?.['medium-width']}
                                                    height={speaker.image.sizes?.['medium-height']}
                                                    fallbackSrc={speaker.image.url || ''}
                                                    className="w-[100px] h-[100px] flex-grow-0 flex-shrink-0 object-cover border border-[#b4b4b4]/[0.35] bg-gray-50"
                                                />
                                            </ExpandableImage>
                                        </div>
                                    )}
                                    <div className="flex flex-col justify-start items-start self-stretch flex-grow relative gap-1">
                                        <p className="flex-grow-0 flex-shrink-0 text-[10px] text-left uppercase text-[#5637cd]">
                                            &nbsp;
                                        </p>
                                        <p className="self-stretch flex-grow-0 flex-shrink-0 w-[181.33px] text-xl text-left text-[#110b29]">
                                            {speaker.name}
                                        </p>
                                        <p className="self-stretch flex-grow-0 flex-shrink-0 w-[181.33px] text-base text-left text-[#6a6a6a]">
                                            {speaker.profession}
                                        </p>
                                    </div>
                                </div>
                            ))}
                    </div>
                </section>
                <section className="py-12">{formId && <GravityForm onSubmit={onFormSubmit} formId={formId} />}</section>
            </div>
        </main>
    );
};

export default WebinarPageLayout;
